import { FC, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon, CloseIcon } from '../../assets/icons/index';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import { Calendar } from '../../components/calendar/index';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/popover/index';
import { Tooltip } from '../../components/tooltip/index';
import { EventHooks, useFormat, useModalState } from '../../hooks/index';
import { capTableStore } from '../../storage/local/index';
import {
  EventTypeBackend,
  isPendingLifecycleStatus,
  SortByType,
  ValuationEvent,
} from '../../types/events.types';
import { CapTableActivityVariant, capTableActivityVariantTitle } from './variables';

export type CapTableSelectProps = {
  companyId: string;
  /**
   * The historical snapshot list is still in flight. The dropdown itself is driven by events, so
   * it opens immediately — but picking a valuation / incorporation resolves against that list, so
   * only those options wait. "Latest" and "Custom date" read their own endpoints and stay usable.
   */
  isSnapshotListLoading?: boolean;
  onSelectCapTable: (valuationOrIncorporationId: string) => void;
  onSelectCustomCapTableDate: (date: Date | null) => void;
};

type CapTableSelectOption = {
  id: string;
  name: ReactNode | string;
};

export const CapTableSelect: FC<CapTableSelectProps> = ({
  onSelectCapTable,
  companyId,
  isSnapshotListLoading = false,
  onSelectCustomCapTableDate,
}) => {
  const customCapTableDate = capTableStore.get()?.customCapTableDate;
  const previousCompanyIdRef = useRef(companyId);

  const [selectedVariant, setSelected] = useState(
    customCapTableDate
      ? capTableActivityVariantTitle[CapTableActivityVariant.CUSTOM_DATE]
      : capTableActivityVariantTitle[CapTableActivityVariant.LATEST_CAP_TABLE],
  );
  const { format } = useFormat();
  const [customDate, setCustomDate] = useState<Date | null>(
    customCapTableDate ? new Date(customCapTableDate) : null,
  );
  const { events } = EventHooks.useEvents({
    companyId,
    typesFilter: [SortByType.VALUATION, SortByType.COMPANY_INCORPORATION],
  });
  const { isOpen, toggler } = useModalState();
  const valuationEvents = events.filter(
    (event) => event.type === EventTypeBackend.VALUATION,
  ) as ValuationEvent[];
  const companyIncorporationEvent = events.find(
    (event) =>
      event.type === EventTypeBackend.COMPANY_INCORPORATION &&
      !isPendingLifecycleStatus(event.lifecycleStatus),
  );

  const selectOptions: CapTableSelectOption[] = useMemo(
    () => [
      {
        name: capTableActivityVariantTitle[CapTableActivityVariant.LATEST_CAP_TABLE],
        id: '',
      },
      ...(companyIncorporationEvent
        ? [
            {
              // Mapper spreads subtype id onto the union (same as Valuation).
              id: companyIncorporationEvent.id,
              name: <AppFormattedMessage id="INCORPORATION" />,
            },
          ]
        : []),
      ...valuationEvents,
      {
        name: capTableActivityVariantTitle[CapTableActivityVariant.CUSTOM_DATE],
        id: 'custom',
      },
    ],
    [companyIncorporationEvent, valuationEvents],
  );

  useEffect(() => {
    if (customCapTableDate) {
      onSelectCustomCapTableDate(new Date(customCapTableDate));
    }
  }, [customCapTableDate, onSelectCustomCapTableDate]);

  useEffect(() => {
    if (previousCompanyIdRef.current === companyId) return;
    previousCompanyIdRef.current = companyId;

    capTableStore.set({ customCapTableDate: null });
    onSelectCustomCapTableDate(null);
    onSelectCapTable('');
    setSelected(capTableActivityVariantTitle[CapTableActivityVariant.LATEST_CAP_TABLE]);
    setCustomDate(null);
  }, [companyId, onSelectCapTable, onSelectCustomCapTableDate]);

  // Only the historical options resolve against the snapshot list; '' is the latest cap table and
  // 'custom' opens the date picker, both backed by their own endpoints.
  const isHistoricalOption = (id: string) => id !== '' && id !== 'custom';

  const handleValuationSelect = ({ id, name }: CapTableSelectOption) => {
    if (isSnapshotListLoading && isHistoricalOption(id)) return;

    setSelected(name);
    if (name === capTableActivityVariantTitle[CapTableActivityVariant.CUSTOM_DATE]) return;

    capTableStore.set({ customCapTableDate: null });
    onSelectCustomCapTableDate(null);
    onSelectCapTable(id);
    toggler(false);
  };

  const handleCustomCapTableDate = (date: Date) => {
    capTableStore.set({ customCapTableDate: date });
    onSelectCustomCapTableDate(date);
    onSelectCapTable('');
    toggler(false);
  };

  return (
    <>
      <Popover onOpenChange={toggler} open={isOpen}>
        <PopoverTrigger className="flex items-center gap-2 rounded border border-gray-200 bg-gray-50 px-3 py-2">
          <span className="text-sm font-[450] text-gray-700">{selectedVariant}</span>
          <ChevronDownIcon className="h-4 w-4 text-gray-400" />
        </PopoverTrigger>
        {selectedVariant === capTableActivityVariantTitle[CapTableActivityVariant.CUSTOM_DATE] ? (
          <PopoverContent className="flex w-auto flex-col gap-4 text-nowrap rounded-lg bg-gray-100 p-0 shadow-xl">
            <div className="flex flex-col gap-4 rounded-b-lg bg-white p-4">
              <div className="flex w-full items-center justify-between pb-4">
                <span className="text-xl font-semibold text-gray-700">
                  <AppFormattedMessage id="CUSTOM_DATE" />
                </span>
                <Button className="h-fit w-fit" onClick={() => toggler(false)} styleType="NONE">
                  <CloseIcon className="size-3" />
                </Button>
              </div>
              <div className="flex flex-col gap-2 text-sm font-[450] text-gray-700">
                <span>
                  <AppFormattedMessage id="DATE" />
                </span>
                <div className="flex h-10 w-full items-center rounded-t border-b border-b-gray-700 bg-gray-50 px-[10px]">
                  {customDate && format(customDate, 'dd/MM/yyyy')}
                </div>
              </div>
              <Calendar
                className="p-0"
                initialFocus
                mode="single"
                onSelect={(date) => {
                  if (!date) return;
                  setCustomDate(date);
                  capTableStore.set({ customCapTableDate: date });
                }}
                selected={customDate || undefined}
                toDate={new Date()}
              />
            </div>
            <div className="flex w-full items-center justify-between px-6 pb-4 text-sm font-[450] text-gray-700">
              <Button
                className="mr-3 h-fit w-fit"
                onClick={() => handleValuationSelect(selectOptions[0])}
                styleType="NONE"
              >
                <AppFormattedMessage id="CANCEL" />
              </Button>
              <div className="flex h-9 gap-3">
                <Button
                  className="rounded border border-gray-300 bg-white px-4"
                  onClick={() => setCustomDate(null)}
                  styleType="NONE"
                >
                  <AppFormattedMessage id="RESET" />
                </Button>
                <Button
                  className="w-fit px-4 font-[550] text-white"
                  onClick={() => customDate && handleCustomCapTableDate(customDate)}
                >
                  <AppFormattedMessage id="SELECT" />
                </Button>
              </div>
            </div>
          </PopoverContent>
        ) : (
          <PopoverContent className="flex max-h-[400px] w-auto max-w-[350px] flex-col gap-2 overflow-y-auto text-nowrap rounded p-2 shadow-xl lg:max-w-[700px]">
            {selectOptions.map((data) => {
              const isDisabled = isSnapshotListLoading && isHistoricalOption(data.id);

              return (
                <span
                  aria-disabled={isDisabled}
                  className={twMerge(
                    'block w-full cursor-pointer rounded px-2 py-[6px] text-sm text-gray-700 hover:bg-brand-25',
                    isDisabled && 'cursor-progress text-gray-300 hover:bg-transparent',
                  )}
                  key={`${data.id}_${data.name}`}
                  onClick={() => handleValuationSelect(data)}
                >
                  {data.id !== 'custom' ? (
                    <Tooltip
                      className="cursor-pointer"
                      content={isDisabled ? <AppFormattedMessage id="LOADING" /> : data.name}
                    >
                      <p
                        className={twMerge(
                          'cursor-pointer truncate',
                          isDisabled && 'cursor-progress',
                        )}
                      >
                        {data.name}
                      </p>
                    </Tooltip>
                  ) : (
                    <p className="truncate">{data.name}</p>
                  )}
                </span>
              );
            })}
          </PopoverContent>
        )}
      </Popover>
    </>
  );
};
